import { useT } from "@transifex/react/dist";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import CancelCreateExerciseModal from "../../../basic/CancelCreateExerciseModal/CancelCreateExerciseModal";
import CloseButton from "../../../basic/closeButton/CloseButton";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useLogger } from "../../../hooks/useLogger";
import ContentWithHeader from "../../../wrapper/ContentWithHeader/ContentWithHeader";
import { getCreationReturnPath } from "../../../helpers/navigationHelper";

import { StickyCloseButtonWrapper } from "./StyledComponents";

interface TestCreationHeaderProps {
    children?: React.ReactNode;
    hasFullHeightContent?: boolean;
}

const TestCreationHeader = (props: TestCreationHeaderProps) => {
    const [cancelCreateExerciseModalIsOpen, setCancelCreateExerciseModalIsOpen] = useState(false);
    const history = useHistory();
    const { resetAppStatus, isTestSynced, loadUserTests } = useTestCreationContext();
    const {
        userInfo,
        setIsAnonFromTeacherArea,
        isAnonFromTeacherArea,
        creatingForSchoolClassId,
        setCreatingForSchoolClassId,
    } = useGeneralContext();
    const { logEvent } = useLogger();

    const t = useT();
    const t_cancelText = t("Cancel", { _tags: "CreateTest,CreateInfoModal" });
    const t_cancelCreateExerciseText = t(
        "The changes to this exercise sheet have not been saved yet. Do you really want to discard them?",
        { _tags: "CreateTest,CancelCreateExercise" }
    );

    const navigateAfterClosingCreation = () => {
        if (isAnonFromTeacherArea) {
            setIsAnonFromTeacherArea(false);
        } else {
            loadUserTests(true);
        }
        const returnPath = getCreationReturnPath(creatingForSchoolClassId);
        setCreatingForSchoolClassId("");
        history.push(returnPath);
        resetAppStatus();
    };

    const closeExerciseSheet = () => {
        if (isTestSynced) {
            navigateAfterClosingCreation();
        } else {
            setCancelCreateExerciseModalIsOpen(true);
        }
    };

    const shouldShowCloseButton = !userInfo.isAnonymousUser || isAnonFromTeacherArea;
    const onLogoClick = shouldShowCloseButton ? closeExerciseSheet : undefined;

    return (
        <ContentWithHeader
            hasGrayBackground
            hasFullHeightContent={props.hasFullHeightContent}
            onLogoClick={onLogoClick}
        >
            {shouldShowCloseButton && (
                <StickyCloseButtonWrapper>
                    <CloseButton
                        onClick={closeExerciseSheet}
                        hintText={t_cancelText}
                    />
                    <CancelCreateExerciseModal
                        isOpen={cancelCreateExerciseModalIsOpen}
                        closeModalCallback={() => {
                            setCancelCreateExerciseModalIsOpen(false);
                        }}
                        continueCallback={() => {
                            logEvent("Test Creation Discarded", { last_step: history.location.pathname });
                            navigateAfterClosingCreation();
                        }}
                        text={t_cancelCreateExerciseText}
                    />
                </StickyCloseButtonWrapper>
            )}
            {props.children}
        </ContentWithHeader>
    );
};

export default TestCreationHeader;
